import { MouseEvent, useEffect, useRef, useState } from 'react';
import { isUndefined } from 'lodash';
import queryString from 'query-string';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { SIDEBAR_ACTIVE_SECTION_KEY } from 'common/constants/userPreferences';
import { useLocalStorageByUser } from 'common/hooks';
import { ListItemsProps } from './ListItems.props';
import ListItemsMenu from '../ListItemsMenu';
import ListMenuSkeleton from '../ListMenuSkeleton';
import { classes, StyledList } from './ListItems.styles';

interface RouteParams {
  companySlugParam?: string;
  fundSlugParam?: string;
}

const ListItems = ({ menuItems, open, setOpenSidebar, setAnchorEl, closeDropDown, setSubMenuItem }: ListItemsProps) => {
  const { companySlugParam, fundSlugParam } = useParams<RouteParams>();
  const navigate = useNavigate();
  const location = useLocation();

  // undefined -> localStorage has not been read yet
  // null -> a valid stored value meaning "no section expanded"
  // string -> a valid stored section slug
  const [storedActive, setStoredActive] = useLocalStorageByUser(SIDEBAR_ACTIVE_SECTION_KEY, undefined);
  const [activeFirstLevel, setActiveFirstLevel] = useState<string | null | undefined>(undefined);

  const { date: dateQueryString } = queryString.parse(location.search);

  const firstLoad = useRef(true);

  const handleMenu = (event: MouseEvent | null, menuItem: any) => {
    closeDropDown();

    // Execute custom callback if provided
    if (menuItem.callback) {
      menuItem.callback();
      return;
    }

    // Nothing to do without a slug
    if (!menuItem.slug) {
      return;
    }

    // If the sidebar is closed, always open it
    if (!open) {
      setOpenSidebar(true);

      // If the section is already expanded, keep it expanded
      // (avoid collapsing due to the same click that opens the sidebar)
      setActiveFirstLevel(prev => {
        if (prev === menuItem.slug) {
          return prev;
        }

        // If the section was collapsed, expand it
        return menuItem.slug;
      });

      return;
    }

    // If the sidebar is already open, allow normal toggle behavior
    if (event) {
      setActiveFirstLevel(prev => (prev === menuItem.slug ? null : menuItem.slug));
    }
  };

  const handleSubMenu = (event: MouseEvent | null, subMenuItem: any) => {
    setOpenSidebar(false);

    if (subMenuItem.callback) {
      subMenuItem.callback();
      return;
    }

    const dateParams = subMenuItem.measurementDateSlug ?? dateQueryString;

    navigate({ pathname: subMenuItem.url, search: `date=${dateParams}` });
  };

  const openDropDown = (event: MouseEvent, menuItem: any, subMenuItem: any) => {
    // Only open dropdown for actual company items
    if (subMenuItem.subMenu) {
      setSubMenuItem({ ...subMenuItem, parent: menuItem });

      const target = event.target as HTMLElement;
      const listItem = target.tagName !== 'LI' ? target.closest('li') : target;

      if (listItem) {
        const element = listItem;
        setAnchorEl(element.getBoundingClientRect(), element);
      }
    } else {
      closeDropDown();
    }
  };

  // Runs exactly ONCE, after menuItems and storedActive are ready.
  useEffect(() => {
    // Run only once
    if (!firstLoad.current) {
      return;
    }

    // Wait until menuItems and storedActive are initialized
    if (!menuItems?.length) {
      return;
    }

    // Wait until storage has been read (undefined means "not loaded yet")
    if (isUndefined(storedActive)) {
      return;
    }

    firstLoad.current = false;

    // Storage already has a previous value (including null)
    // Restore whatever was saved and exit
    setActiveFirstLevel(storedActive);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [menuItems]);

  // Persist user choice to local storage whenever it changes.
  // This stores values like: "funds", "companies", null (meaning collapsed)
  useEffect(() => {
    // Avoid writing undefined (means "not initialized yet")
    if (!isUndefined(activeFirstLevel)) {
      setStoredActive(activeFirstLevel);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeFirstLevel]);

  return (
    <StyledList className={classes.root}>
      {menuItems.map(menuItem =>
        !menuItem.subMenu ? (
          <ListMenuSkeleton key={menuItem.slug} open={open ?? false} classes={classes} />
        ) : (
          <ListItemsMenu
            key={menuItem.slug}
            menuItem={menuItem}
            open={open ?? false}
            activeFirstLevel={activeFirstLevel ?? undefined}
            handleMenu={handleMenu}
            classes={classes}
            openDropDown={openDropDown}
            handleSubMenu={handleSubMenu}
            companySlugParam={companySlugParam}
            fundSlugParam={fundSlugParam}
          />
        )
      )}
    </StyledList>
  );
};

export default ListItems;
